import { create } from 'zustand';
import type { PviGraphWithSpans, PviCompileResult } from '../editor/pviTypes';

interface PviPanelState {
  /** Whether the PVI panel is open. */
  panelOpen: boolean;
  /** Panel width in rem. */
  panelWidth: number;
  /** Active tab: graph | table | json. */
  activeTab: 'graph' | 'table' | 'json';
  /** Current compile result (latest successful or error). */
  compileResult: PviCompileResult;
  /** Last valid graph (kept when current source has errors). */
  lastValidGraph: PviGraphWithSpans | null;
  /** Currently highlighted node ID (from source or graph interaction). */
  highlightedNodeId: number | null;
  /** Currently highlighted source line (from graph interaction). */
  highlightedLine: number | null;

  togglePanel: () => void;
  setPanelOpen: (open: boolean) => void;
  setPanelWidth: (width: number) => void;
  setActiveTab: (tab: 'graph' | 'table' | 'json') => void;
  setCompileResult: (result: PviCompileResult) => void;
  setHighlightedNodeId: (id: number | null) => void;
  setHighlightedLine: (line: number | null) => void;
}

export const usePviStore = create<PviPanelState>((set) => ({
  panelOpen: true,
  panelWidth: 40,
  activeTab: 'graph',
  compileResult: {
    status: 'idle',
    graph: null,
    errors: [],
    warnings: [],
    timestamp: 0,
    stale: false,
  },
  lastValidGraph: null,
  highlightedNodeId: null,
  highlightedLine: null,

  togglePanel: () => set(s => ({ panelOpen: !s.panelOpen })),
  setPanelOpen: (open) => set({ panelOpen: open }),
  setPanelWidth: (width) => set({ panelWidth: Math.max(18, Math.min(64, width)) }),
  setActiveTab: (tab) => set({ activeTab: tab }),

  setCompileResult: (result) => {
    const update: Partial<PviPanelState> = { compileResult: result };
    if (result.status === 'success' && result.graph && result.graph.nodes.length > 0) {
      update.lastValidGraph = result.graph;
    }
    set(update);
  },

  setHighlightedNodeId: (id) => set({ highlightedNodeId: id }),
  setHighlightedLine: (line) => set({ highlightedLine: line }),
}));
